import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import useDashboardLayout from './useDashboardLayout'
import useIsNarrow from './useIsNarrow'
import DashboardCanvas from './DashboardCanvas'
import WidgetPalette from './WidgetPalette'
import { ink, surface, line, radius, font, transition, bp } from '../tokens'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const Caption = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  border: 1px solid ${line.base};
  background: ${surface.wash};
  color: ${ink.base};
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
`

const SourceDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${ink.mute};
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 14px;
  border-radius: ${radius.sm};
  border: 1px solid ${line.base};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${line.strong};
  }
`

const PrimaryButton = styled(Button)`
  border-color: ${ink.base};
  background: ${ink.base};
  color: ${surface.card};

  &:hover {
    border-color: ${ink.base};
    background: ${ink.soft};
  }
`

const TextButton = styled(Button)`
  border-color: transparent;
  background: transparent;
  color: ${ink.mute};

  &:hover {
    border-color: transparent;
    color: ${ink.base};
  }
`

const Body = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.$withPalette ? 'minmax(0, 1fr) 280px' : 'minmax(0, 1fr)')};
  gap: 16px;
  align-items: start;

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const Loading = styled.div`
  padding: 32px;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`

const SOURCE_LABELS = {
  user: 'Your view',
  org: 'Organisation default',
  system: 'Standard layout',
}

const CustomisableDashboard = ({ altitude = 'portfolio', nodes, filterOptions, hiddenWidgetKeys = [] }) => {
  const {
    layout,
    widgets,
    source,
    canEditOrgDefault,
    orgLayoutAvailable,
    loading,
    setLayout,
    savePersonal,
    saveOrg,
    reset,
  } = useDashboardLayout(altitude)

  const [editing, setEditing] = useState(false)
  const [showPalette, setShowPalette] = useState(false)
  const [snapshot, setSnapshot] = useState(null)

  const isNarrow = useIsNarrow()

  const visibleLayout = useMemo(() => {
    const known = new Set(widgets.map(widget => widget.key))
    return layout.filter(item => known.has(item.widgetKey) && !hiddenWidgetKeys.includes(item.widgetKey))
  }, [layout, widgets, hiddenWidgetKeys])
  const visibleWidgets = useMemo(
    () => widgets.filter(widget => !hiddenWidgetKeys.includes(widget.key)),
    [widgets, hiddenWidgetKeys]
  )

  const handleLayoutChange = useCallback(
    next => {
      setLayout(prev => {
        const hidden = prev.filter(item => hiddenWidgetKeys.includes(item.widgetKey))
        return [...next, ...hidden]
      })
    },
    [hiddenWidgetKeys, setLayout]
  )

  const startEditing = useCallback(() => {
    setSnapshot(layout)
    setEditing(true)
  }, [layout])

  const handleSave = useCallback(async () => {
    const ok = await savePersonal()
    if (ok) {
      setEditing(false)
      setShowPalette(false)
    }
  }, [savePersonal])

  const handleCancel = useCallback(() => {
    if (snapshot) setLayout(snapshot)
    setEditing(false)
    setShowPalette(false)
  }, [snapshot, setLayout])

  const handleRemove = useCallback(
    widgetKey => {
      setLayout(prev => prev.filter(item => item.widgetKey !== widgetKey))
    },
    [setLayout]
  )

  const handleAdd = useCallback(
    widget => {
      setLayout(prev => {
        const maxY = prev.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)
        return [
          ...prev,
          {
            widgetKey: widget.key,
            x: 0,
            y: maxY,
            w: widget.defaultW,
            h: widget.defaultH,
            settings: undefined,
          },
        ]
      })
    },
    [setLayout]
  )

  const sourceLabel = useMemo(
    () => SOURCE_LABELS[source] || SOURCE_LABELS.system,
    [source]
  )

  const handleSaveOrg = useCallback(() => {
    const ok = window.confirm(
      "Save as your organisation's default. Colleagues without their own layout will see this."
    )
    if (ok) saveOrg()
  }, [saveOrg])

  const showUseOrgLayout = source === 'user' && orgLayoutAvailable

  if (loading) {
    return <Loading>Loading your dashboard…</Loading>
  }

  const palette = editing && showPalette
    ? <WidgetPalette widgets={visibleWidgets} layout={visibleLayout} onAdd={handleAdd} />
    : null

  return (
    <Wrapper>
      <HeaderRow>
        <HeaderLeft>
          <SourceBadge title="Where this dashboard layout comes from">
            <SourceDot aria-hidden="true" />
            {sourceLabel}
          </SourceBadge>
          {editing && (
            <Caption>
              {isNarrow
                ? 'Add or remove widgets here. Rearranging and resizing are available on a larger screen.'
                : 'Drag a widget to move it, drag its corner to resize. Your changes apply when you Save.'}
            </Caption>
          )}
        </HeaderLeft>
        <Actions>
          {!editing ? (
            <>
              {showUseOrgLayout && (
                <Button type="button" onClick={reset}>
                  Use organisation&apos;s layout
                </Button>
              )}
              <Button type="button" onClick={startEditing}>
                Customise
              </Button>
            </>
          ) : (
            <>
              <Button type="button" onClick={() => setShowPalette(prev => !prev)}>
                Add widget
              </Button>
              {showUseOrgLayout ? (
                <Button type="button" onClick={reset}>
                  Use organisation&apos;s layout
                </Button>
              ) : (
                <Button type="button" onClick={reset}>
                  Reset to standard layout
                </Button>
              )}
              {canEditOrgDefault && !isNarrow && (
                <Button type="button" onClick={handleSaveOrg}>
                  Save as organisation default
                </Button>
              )}
              <TextButton type="button" onClick={handleCancel}>
                Cancel
              </TextButton>
              <PrimaryButton type="button" onClick={handleSave}>
                Save changes
              </PrimaryButton>
            </>
          )}
        </Actions>
      </HeaderRow>

      <Body $withPalette={!isNarrow && editing && showPalette}>
        {isNarrow && palette}
        <DashboardCanvas
          layout={visibleLayout}
          widgets={visibleWidgets}
          editing={editing}
          isNarrow={isNarrow}
          nodes={nodes}
          filterOptions={filterOptions}
          onLayoutChange={handleLayoutChange}
          onRemove={handleRemove}
        />
        {!isNarrow && palette}
      </Body>
    </Wrapper>
  )
}

export default CustomisableDashboard
